// services/api.js - Cloudflare Workers API client with R2 file support
import axios from 'axios';
import i18n from '../i18n'; // ✅ PŘIDÁNO: Import i18n pro automatické posílání jazyka

// ✅ API BASE URL CONFIGURATION
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://project-management-api-modular-prod.yqj6bh8w4c.workers.dev'  // ✅ Updated to new backend
  : 'http://localhost:8787';

// ✅ EXPORT API_BASE_URL hned po definici
export { API_BASE_URL };

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ✅ PŘIDÁNO: Funkce pro získání aktuálního jazyka
const getCurrentLanguage = () => {
  try {
    return i18n.language || 'en';
  } catch (error) {
    console.warn('Could not get current language from i18n:', error);
    return 'en';
  }
};

// ✅ PŘIDÁNO: Funkce pro vytvoření URL s jazykem
const addLanguageToUrl = (url) => {
  const currentLanguage = getCurrentLanguage();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}lang=${currentLanguage}`;
};

// ✅ REQUEST INTERCEPTOR - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ RESPONSE INTERCEPTOR - Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 🔐 AUTH API
export const auth = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};

// 📊 PROJECTS API
export const projects = {
  getAll: () => api.get('/api/projects'),
  getById: (id) => api.get(`/api/projects/${id}`),
  create: (projectData) => api.post('/api/projects', projectData),
  update: (id, projectData) => api.put(`/api/projects/${id}`, projectData),
  delete: (id) => api.delete(`/api/projects/${id}`),
  
  // Work logs
  addWorkLog: (projectId, workLogData) => api.post(`/api/projects/${projectId}/work-log`, workLogData),
  updateWorkLog: (projectId, logId, workLogData) => api.put(`/api/projects/${projectId}/work-log/${logId}`, workLogData),
  deleteWorkLog: (projectId, logId) => api.delete(`/api/projects/${projectId}/work-log/${logId}`),
  
  // ✅ FILES API - PŘIDANÉ chybějící metody
  // Modern FormData upload with progress tracking
  addFile: (projectId, formData, progressCallback) => {
    return api.post(`/api/projects/${projectId}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressCallback && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(progress);
        }
      }
    });
  },
  
  // Legacy base64 upload (backward compatibility)
  addFileLegacy: (projectId, fileData) => api.post(`/api/projects/${projectId}/file`, fileData),
  
  // Existing methods (zachované z původního souboru)
  uploadFile: (projectId, formData) => api.post(`/api/projects/${projectId}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFileLegacy: (projectId, fileData) => api.post(`/api/projects/${projectId}/file`, fileData),
  deleteFile: (projectId, fileId) => api.delete(`/api/projects/${projectId}/file/${fileId}`),
  downloadFile: (projectId, fileId) => api.get(`/api/projects/${projectId}/file/${fileId}/download`, {
    responseType: 'blob'
  }),
  updateFile: (projectId, fileId, fileData) => api.put(`/api/projects/${projectId}/file/${fileId}`, fileData),

  // ✅ SIGNATURES R2 API
  addSignature: (projectId, signatureData) => api.post(`/api/projects/${projectId}/signature`, signatureData),
  updateSignature: (projectId, signatureData) => api.put(`/api/projects/${projectId}/signature`, signatureData),
  deleteSignature: (projectId) => api.delete(`/api/projects/${projectId}/signature`),
  downloadSignature: (projectId) => api.get(`/api/projects/${projectId}/signature/download`, {
    responseType: 'blob'
  }),
  
  // ✅ Get signature as base64 (pro backward compatibility)
  getSignatureBase64: (projectId) => api.get(`/api/projects/${projectId}/signature/base64`)
};

// 👥 USERS API
export const users = {
  getAll: () => api.get('/api/users'),
  getById: (id) => api.get(`/api/users/${id}`),
  create: (userData) => api.post('/api/users', userData),
  update: (id, userData) => api.put(`/api/users/${id}`, userData),
  delete: (id) => api.delete(`/api/users/${id}`),
  
  // ✅ S PODROBNÝM DEBUGGING
  changePassword: async (passwords) => {
    try {
      console.log('🔍 Frontend: Attempting password change...');
      console.log('🔍 Request data:', {
        currentPassword: '***',
        newPassword: '***',
        newPasswordLength: passwords.new?.length
      });
      
      const response = await api.post('/api/users/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
      
      console.log('✅ Frontend: Password change SUCCESS');
      console.log('📝 Response:', response.data);
      return response;
      
    } catch (error) {
      console.error('❌ Frontend: Password change ERROR');
      console.error('📝 Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        headers: error.response?.headers
      });
      
      // Zobraz konkrétní chybovou zprávu z serveru
      if (error.response?.data?.message) {
        console.error('🔴 Server error message:', error.response.data.message);
      }
      
      throw error;
    }
  }
};

// 🏢 COMPANY ROLES API
export const companyRoles = {
  getAll: () => api.get('/api/company-roles'),
  create: (roleData) => api.post('/api/company-roles', roleData),
  update: (id, roleData) => api.put(`/api/company-roles/${id}`, roleData),
  delete: (id) => api.delete(`/api/company-roles/${id}`)
};

// 🏠 WAREHOUSES API
export const warehouses = {
  getAll: () => api.get('/api/warehouses'),
  getById: (id) => api.get(`/api/warehouses/${id}`),
  create: (warehouseData) => api.post('/api/warehouses', warehouseData),
  update: (id, warehouseData) => api.put(`/api/warehouses/${id}`, warehouseData),
  delete: (id) => api.delete(`/api/warehouses/${id}`),
  
  // Material management within warehouses
  addMaterial: (warehouseId, materialData) => api.post(`/api/warehouses/${warehouseId}/materials`, materialData),
  updateMaterial: (warehouseId, materialId, materialData) => api.put(`/api/warehouses/${warehouseId}/materials/${materialId}`, materialData),
  deleteMaterial: (warehouseId, materialId) => api.delete(`/api/warehouses/${warehouseId}/materials/${materialId}`),
  
  // Assignment operations
  assignMaterial: (warehouseId, assignmentData) => api.post(`/api/warehouses/${warehouseId}/assign`, assignmentData),
  getAssignments: (warehouseId) => api.get(`/api/warehouses/${warehouseId}/assignments`),
  updateAssignment: (warehouseId, assignmentData) => api.put(`/api/warehouses/${warehouseId}/assign`, assignmentData),
  removeAssignment: (warehouseId, assignmentData) => api.delete(`/api/warehouses/${warehouseId}/assign`, { data: assignmentData })
};

// 👤 CUSTOMERS API
export const customers = {
  getAll: () => api.get('/api/customers'),
  getById: (id) => api.get(`/api/customers/${id}`),
  create: (customerData) => api.post('/api/customers', customerData),
  update: (id, customerData) => api.put(`/api/customers/${id}`, customerData),
  delete: (id) => api.delete(`/api/customers/${id}`)
};

// 📦 MATERIALS API (existing)
export const materials = {
  // Material receipts
  getReceipts: () => api.get('/api/material-receipts'),
  getReceiptById: (id) => api.get(`/api/material-receipts/${id}`),
  createReceipt: (receiptData) => api.post('/api/material-receipts', receiptData),
  updateReceipt: (id, receiptData) => api.put(`/api/material-receipts/${id}`, receiptData),
  deleteReceipt: (id) => api.delete(`/api/material-receipts/${id}`),
  
  // Material deliveries
  getDeliveries: () => api.get('/api/material-deliveries'),
  getDeliveryById: (id) => api.get(`/api/material-deliveries/${id}`),
  createDelivery: (deliveryData) => api.post('/api/material-deliveries', deliveryData),
  updateDelivery: (id, deliveryData) => api.put(`/api/material-deliveries/${id}`, deliveryData),
  deleteDelivery: (id) => api.delete(`/api/material-deliveries/${id}`),
  
  // Warehouse items
  getItems: () => api.get('/api/warehouse-items'),
  getItemById: (id) => api.get(`/api/warehouse-items/${id}`),
  createItem: (itemData) => api.post('/api/warehouse-items', itemData),
  updateItem: (id, itemData) => api.put(`/api/warehouse-items/${id}`, itemData),
  deleteItem: (id) => api.delete(`/api/warehouse-items/${id}`)
};

// ⚙️ SETTINGS API - ✅ UPRAVENO: s automatickým jazykem
export const companySettings = {
  get: () => api.get(addLanguageToUrl('/api/settings/company')),
  update: (settingsData) => api.put(addLanguageToUrl('/api/settings/company'), settingsData),
  
  // Currencies
  getCurrencies: () => api.get('/api/settings/currencies'),
  updateCurrencies: (currenciesData) => api.put('/api/settings/currencies', currenciesData)
};

// 🏷️ FILE TAGS API - ✅ UPRAVENO: s automatickým jazykem
export const fileTags = {
  getAll: () => api.get(addLanguageToUrl('/api/settings/file-tags')),
  update: (fileTagsData) => api.put(addLanguageToUrl('/api/settings/file-tags'), fileTagsData)
};

// 🏭 WAREHOUSE ITEMS API (dle handover požadavků)
export const warehouseItems = {
  getAll: () => api.get('/api/warehouse-items'),
  getById: (id) => api.get(`/api/warehouse-items/${id}`),
  create: (itemData) => api.post('/api/warehouse-items', itemData),
  update: (id, itemData) => api.put(`/api/warehouse-items/${id}`, itemData),
  delete: (id) => api.delete(`/api/warehouse-items/${id}`),
  
  // Warehouse assignments
  assignToWarehouse: (itemId, warehouseId, amount) => 
    api.post(`/api/warehouse-items/${itemId}/assign`, { warehouseId, amount }),
  removeFromWarehouse: (itemId, warehouseId) => 
    api.delete(`/api/warehouse-items/${itemId}/warehouse/${warehouseId}`),
  getWarehouseAssignments: (itemId) => 
    api.get(`/api/warehouse-items/${itemId}/assignments`)
};

// 📦 MATERIAL RECEIPTS API (dle handover požadavků)
export const materialReceipts = {
  getAll: () => api.get('/api/material-receipts'),
  getById: (id) => api.get(`/api/material-receipts/${id}`),
  create: (receiptData) => api.post('/api/material-receipts', receiptData),
  update: (id, receiptData) => api.put(`/api/material-receipts/${id}`, receiptData),
  delete: (id) => api.delete(`/api/material-receipts/${id}`),
  
  // Receipt items
  addItem: (receiptId, itemData) => api.post(`/api/material-receipts/${receiptId}/items`, itemData),
  updateItem: (receiptId, itemId, itemData) => api.put(`/api/material-receipts/${receiptId}/items/${itemId}`, itemData),
  deleteItem: (receiptId, itemId) => api.delete(`/api/material-receipts/${receiptId}/items/${itemId}`)
};

// 🚚 MATERIAL DELIVERIES API (dle handover požadavků)
export const materialDeliveries = {
  getAll: () => api.get('/api/material-deliveries'),
  getById: (id) => api.get(`/api/material-deliveries/${id}`),
  create: (deliveryData) => api.post('/api/material-deliveries', deliveryData),
  update: (id, deliveryData) => api.put(`/api/material-deliveries/${id}`, deliveryData),
  delete: (id) => api.delete(`/api/material-deliveries/${id}`),
  
  // Delivery items
  addItem: (deliveryId, itemData) => api.post(`/api/material-deliveries/${deliveryId}/items`, itemData),
  updateItem: (deliveryId, itemId, itemData) => api.put(`/api/material-deliveries/${deliveryId}/items/${itemId}`, itemData),
  deleteItem: (deliveryId, itemId) => api.delete(`/api/material-deliveries/${deliveryId}/items/${itemId}`)
};

// 📊 DATA USAGE API - NOVÁ SEKCE
export const dataUsage = {
  get: () => api.get('/api/settings/data-usage')
};

// Export default api instance for direct use
export default api;

// ✅ DEBUG EXPORT PRO DEVELOPMENT
if (process.env.NODE_ENV === 'development') {
  window.api = api;
  window.apiDebug = {
    api: api,
    projects: projects,
    users: users,
    warehouses: warehouses,
    customers: customers,
    materials: materials,
    warehouseItems: warehouseItems,
    materialReceipts: materialReceipts,
    materialDeliveries: materialDeliveries,
    
    // ✅ BASIC TEST FUNCTIONS
    testAuth: () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      console.log('🔐 Auth token:', token ? 'Present' : 'Missing');
      console.log('👤 User data:', user ? JSON.parse(user) : 'Missing');
      return { token: !!token, user: !!user };
    },
    
    testProject: async (id = 2) => {
      try {
        const response = await projects.getById(id);
        console.log('✅ Project:', response.data);
        console.log('📁 Files:', response.data.files?.length || 0);
        response.data.files?.forEach((file, i) => {
          console.log(`File ${i}:`, {
            id: file.id || file._id,
            name: file.fileName || file.file_name,
            type: file.fileType || file.file_type,
            hasBase64: !!file.base64,
            hasR2Key: !!file.r2_key,
            fileSize: file.file_size
          });
        });
        
        // Test signature
        if (response.data.signature) {
          const sig = typeof response.data.signature === 'string' 
            ? JSON.parse(response.data.signature) 
            : response.data.signature;
          console.log('🖋️ Signature:', {
            hasBase64: !!sig.base64,
            hasR2Key: !!sig.r2_key,
            signatureDate: sig.date
          });
        }
        
        return response.data;
      } catch (error) {
        console.error('❌ Project test failed:', error);
        return null;
      }
    },
    
    // ✅ NEW: Test file upload
    testFileUpload: async (projectId = 2) => {
      try {
        console.log(`📁 Testing file upload to project ${projectId}`);
        
        // Create a simple test file
        const testContent = 'Test file content for R2 upload';
        const testFile = new File([testContent], 'test-file.txt', {
          type: 'text/plain'
        });
        
        // Create FormData
        const formData = new FormData();
        formData.append('file', testFile);
        formData.append('tags', 'document');
        formData.append('description', 'Test file upload');
        
        console.log('📤 Uploading test file...');
        
        const response = await projects.addFile(projectId, formData, (progress) => {
          console.log(`📊 Upload progress: ${progress}%`);
        });
        
        console.log('✅ File upload successful:', response.data);
        return response.data;
        
      } catch (error) {
        console.error('❌ File upload test failed:', error);
        return null;
      }
    },
    
    testImageDownload: async (projectId = 2, fileId = 10) => {
      try {
        console.log(`🖼️ Testing image download for project ${projectId}, file ${fileId}`);
        
        // First, get project to see file details
        const projectResponse = await projects.getById(projectId);
        const targetFile = projectResponse.data.files?.find(f => (f.id || f._id) == fileId);
        
        if (targetFile) {
          console.log('📄 Target file details:', {
            id: targetFile.id || targetFile._id,
            name: targetFile.fileName || targetFile.originalName || targetFile.file_name,
            type: targetFile.fileType || targetFile.file_type,
            hasBase64: !!targetFile.base64,
            hasR2Key: !!targetFile.r2_key,
            fileSize: targetFile.file_size
          });
        }
        
        // Test download
        const response = await projects.downloadFile(projectId, fileId);
        
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        console.log('✅ Image download successful:', {
          size: blob.size,
          type: blob.type,
          url: url.substring(0, 50) + '...'
        });
        
        // Cleanup
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        return { blob, url, file: targetFile };
      } catch (error) {
        console.error('❌ Image download test failed:', error);
        return null;
      }
    },
    
    testSignature: async (projectId = 2) => {
      try {
        console.log(`🖋️ Testing signature for project ${projectId}`);
        const response = await projects.getById(projectId);
        
        if (!response.data.signature) {
          console.log('⚠️ No signature found for this project');
          return null;
        }
        
        const signature = typeof response.data.signature === 'string' 
          ? JSON.parse(response.data.signature) 
          : response.data.signature;
          
        console.log('✅ Signature found:', {
          hasBase64: !!signature.base64,
          hasR2Key: !!signature.r2_key,
          date: signature.date,
          base64Length: signature.base64?.length || 0
        });
        
        return signature;
      } catch (error) {
        console.error('❌ Signature test failed:', error);
        return null;
      }
    },
    
    testSignatureDownload: async (projectId = 2) => {
      try {
        console.log(`🖋️ Testing signature download for project ${projectId}`);
        const response = await projects.downloadSignature(projectId);
        
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        console.log('✅ Signature download successful:', {
          size: blob.size,
          type: blob.type,
          url: url.substring(0, 50) + '...'
        });
        
        // Cleanup
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        return url;
      } catch (error) {
        console.error('❌ Signature download test failed:', error);
        return null;
      }
    },
    
    testSignatureBase64: async (projectId = 2) => {
      try {
        console.log(`🖋️ Testing signature base64 for project ${projectId}`);
        const response = await projects.getSignatureBase64(projectId);
        
        console.log('✅ Signature base64 loaded:', {
          hasBase64: !!response.data.base64,
          hasR2Key: !!response.data.r2_key,
          base64Length: response.data.base64?.length || 0,
          r2Key: response.data.r2_key,
          fileSize: response.data.file_size
        });
        
        return response.data;
      } catch (error) {
        console.error('❌ Signature base64 test failed:', error);
        return null;
      }
    },
    
    // ✅ COMPREHENSIVE TEST SUITE
    runFullTest: async (projectId = 2) => {
      console.log('🧪 Running comprehensive API test suite...');
      
      // 1. Test authentication
      console.log('\n1️⃣ Testing authentication...');
      const authResult = window.apiDebug.testAuth();
      
      if (!authResult.token || !authResult.user) {
        console.log('❌ Authentication failed - please login first');
        return;
      }
      
      // 2. Test project loading
      console.log('\n2️⃣ Testing project loading...');
      const project = await window.apiDebug.testProject(projectId);
      
      if (!project) {
        console.log('❌ Project loading failed');
        return;
      }
      
      // 3. Test file upload
      console.log('\n3️⃣ Testing file upload...');
      const uploadResult = await window.apiDebug.testFileUpload(projectId);
      
      if (uploadResult) {
        console.log('✅ File upload test successful');
      } else {
        console.log('❌ File upload test failed');
      }
      
      // 4. Test image downloads
      if (project.files && project.files.length > 0) {
        console.log('\n4️⃣ Testing image downloads...');
        const firstImageFile = project.files.find(f => 
          (f.fileType || f.file_type || '').startsWith('image/') || !(f.fileType || f.file_type)
        );
        
        if (firstImageFile) {
          const downloadResult = await window.apiDebug.testImageDownload(
            projectId, 
            firstImageFile.id || firstImageFile._id
          );
          
          if (downloadResult) {
            console.log('💡 FilePreview tip: Open FilePreview modal and check browser console for image loading messages');
          }
        } else {
          console.log('⚠️ No image files found in this project');
        }
      }
      
      // 5. Test signature functionality
      console.log('\n5️⃣ Testing signature functionality...');
      await window.apiDebug.testSignature(projectId);
      await window.apiDebug.testSignatureDownload(projectId);
      await window.apiDebug.testSignatureBase64(projectId);
      
      console.log('\n🎉 Comprehensive test completed!');
      console.log('\n🔧 FilePreview Debug Tips:');
      console.log('- Open a project and click on an image');
      console.log('- Check browser console for "📷 FilePreview:" messages');
      console.log('- Look for "✅ FilePreview: Image file loaded from R2" success message');
      console.log('- If you see "📄 FilePreview: Non-image file", check the file.fileType property');
    },
    
    // ✅ NEW: FilePreview specific test
    testFilePreview: async (projectId = 2) => {
      console.log('🖼️ Testing FilePreview functionality...');
      
      const project = await window.apiDebug.testProject(projectId);
      if (!project || !project.files || project.files.length === 0) {
        console.log('❌ No files found in project');
        return;
      }
      
      console.log('\n📄 File analysis for FilePreview:');
      project.files.forEach((file, i) => {
        const fileType = file.fileType || file.file_type || '';
        const isImage = fileType.startsWith('image/') || !fileType;
        
        console.log(`File ${i + 1}:`, {
          id: file.id || file._id,
          name: file.fileName || file.originalName || file.file_name,
          type: fileType,
          isImage: isImage,
          hasBase64: !!file.base64,
          hasR2Key: !!file.r2_key,
          willLoadInPreview: isImage
        });
      });
      
      const imageFiles = project.files.filter(f => {
        const fileType = f.fileType || f.file_type || '';
        return fileType.startsWith('image/') || !fileType;
      });
      
      console.log(`\n📊 Summary: ${imageFiles.length}/${project.files.length} files will load in FilePreview`);
      
      if (imageFiles.length > 0) {
        console.log('\n💡 To test FilePreview:');
        console.log('1. Click on any image in the project');
        console.log('2. FilePreview modal should open');
        console.log('3. Watch console for loading messages');
        console.log('4. Image should appear in modal (not just metadata)');
      }
      
      return { project, imageFiles };
    },
    
    // ✅ NEW: Advanced signature testing
    testSignatureFlow: async (projectId = 2) => {
      console.log('🔄 Testing complete signature flow...');
      
      try {
        // 1. Get current state
        console.log('\n📋 Step 1: Current signature state');
        const project = await window.apiDebug.testProject(projectId);
        const currentSig = project.signature;
        
        if (currentSig) {
          const sig = typeof currentSig === 'string' ? JSON.parse(currentSig) : currentSig;
          console.log('Current signature:', {
            hasR2Key: !!sig.r2_key,
            hasBase64: !!sig.base64,
            loadingMethod: sig.r2_key ? 'R2 Storage' : (sig.base64 ? 'Base64 Legacy' : 'None')
          });
        } else {
          console.log('No signature exists');
        }
        
        // 2. Create test signature
        console.log('\n💾 Step 2: Creating test signature...');
        const testSignature = {
          base64: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
          date: new Date().toISOString()
        };
        
        const saveResponse = await projects.addSignature(projectId, testSignature);
        console.log('✅ Signature saved:', saveResponse.data);
        
        // 3. Wait a moment for backend processing
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // 4. Test new state
        console.log('\n📋 Step 3: New signature state');
        const updatedProject = await window.apiDebug.testProject(projectId);
        const newSig = updatedProject.signature;
        
        if (newSig) {
          const sig = typeof newSig === 'string' ? JSON.parse(newSig) : newSig;
          console.log('New signature:', {
            hasR2Key: !!sig.r2_key,
            hasBase64: !!sig.base64,
            r2Key: sig.r2_key,
            fileSize: sig.file_size,
            loadingMethod: sig.r2_key ? 'R2 Storage' : (sig.base64 ? 'Base64 Legacy' : 'None')
          });
          
          // 5. Test download
          if (sig.r2_key) {
            console.log('\n📥 Step 4: Testing R2 download...');
            await window.apiDebug.testSignatureDownload(projectId);
          }
        }
        
        console.log('\n🎉 Signature flow test completed successfully!');
        return { success: true, signature: newSig };
        
      } catch (error) {
        console.error('❌ Signature flow test failed:', error);
        return { success: false, error: error.message };
      }
    },
    
    // ✅ NEW: Test signature persistence across page reload
    testSignaturePersistence: async (projectId = 2) => {
      console.log('🔄 Testing signature persistence...');
      
      try {
        // Get current signature
        const project = await window.apiDebug.testProject(projectId);
        
        if (!project.signature) {
          console.log('❌ No signature found. Create one first with testSignatureFlow()');
          return false;
        }
        
        const sig = typeof project.signature === 'string' ? JSON.parse(project.signature) : project.signature;
        
        console.log('📋 Signature persistence check:', {
          hasR2Key: !!sig.r2_key,
          hasBase64: !!sig.base64,
          fileSize: sig.file_size,
          shouldPersist: !!sig.r2_key
        });
        
        if (sig.r2_key) {
          console.log('✅ Signature has R2 key - should persist across page reloads');
          console.log('🔄 To test: Refresh the page and check if signature still loads');
          console.log('   Expected: Console should show "📥 Loading signature from R2"');
          return true;
        } else if (sig.base64) {
          console.log('⚠️ Signature uses base64 - will persist but not using R2');
          console.log('💡 Tip: Save a new signature to migrate to R2 storage');
          return false;
        } else {
          console.log('❌ Signature has no data - will not persist');
          return false;
        }
        
      } catch (error) {
        console.error('❌ Persistence test failed:', error);
        return false;
      }
    },
    
    // ✅ PŘIDÁNO: Test jazykové podpory
    testLanguageSupport: async () => {
      console.log('🌍 Testing language support...');
      
      try {
        const currentLanguage = getCurrentLanguage();
        console.log('Current language:', currentLanguage);
        
        // Test file tags with different languages
        console.log('\n🏷️ Testing file tags with current language...');
        const fileTagsResponse = await fileTags.getAll();
        
        console.log('File tags response:', {
          count: fileTagsResponse.data?.length || 0,
          language: currentLanguage,
          firstTag: fileTagsResponse.data?.[0]
        });
        
        // Test company settings
        console.log('\n🏢 Testing company settings with current language...');
        const companyResponse = await companySettings.get();
        
        console.log('Company settings response:', {
          fileTagsCount: companyResponse.data?.fileTags?.length || 0,
          language: currentLanguage,
          firstFileTag: companyResponse.data?.fileTags?.[0]
        });
        
        console.log('\n✅ Language support test completed!');
        console.log('💡 To test different languages:');
        console.log('1. Change language in app UI');
        console.log('2. Run this test again');
        console.log('3. Compare the `label` fields in responses');
        
        return {
          currentLanguage,
          fileTags: fileTagsResponse.data,
          companySettings: companyResponse.data
        };
        
      } catch (error) {
        console.error('❌ Language support test failed:', error);
        return null;
      }
    }
  };
}